import { useMemo } from "react";
import type {
  ZendeskPriority,
  ZendeskStatus,
  ZendeskTicketType,
} from "./ZendeskSourceSettings.types";

export interface ZendeskSourceSettingsText {
  accountLabelText: string;
  accountHintText: string;
  modeLabelText: string;
  modeHintText: string;
  filterModeLabelText: string;
  viewModeLabelText: string;
  statusesLabelText: string;
  statusesPlaceholderText: string;
  statusesHintText: string;
  statusesRequiredText: string;
  prioritiesLabelText: string;
  prioritiesPlaceholderText: string;
  prioritiesHintText: string;
  ticketTypesLabelText: string;
  ticketTypesPlaceholderText: string;
  ticketTypesHintText: string;
  assignedToMeLabelText: string;
  assignedToMeHintText: string;
  tagsLabelText: string;
  tagsPlaceholderText: string;
  tagsHintText: string;
  viewLabelText: string;
  viewPlaceholderText: string;
  viewHintText: string;
  viewRequiredText: string;
  noViewsText: string;
  doneStatusLabelText: string;
  doneStatusPlaceholderText: string;
  doneStatusHintText: string;
  doneStatusRequiredText: string;
  loadingViewsText: string;
  loadErrorText: string;
  statusLabels: Record<ZendeskStatus, string>;
  priorityLabels: Record<ZendeskPriority, string>;
  ticketTypeLabels: Record<ZendeskTicketType, string>;
}

export const useZendeskSourceSettingsText = (
  t: (key: string) => string,
): ZendeskSourceSettingsText => {
  return useMemo(() => {
    const tKey = (key: string): string =>
      t(`board.connections.dialog.sourceSettings.zendesk.${key}`);

    return {
      accountLabelText: tKey("accountLabel"),
      accountHintText: tKey("accountHint"),
      modeLabelText: tKey("modeLabel"),
      modeHintText: tKey("modeHint"),
      filterModeLabelText: tKey("filterModeLabel"),
      viewModeLabelText: tKey("viewModeLabel"),
      statusesLabelText: tKey("statusesLabel"),
      statusesPlaceholderText: tKey("statusesPlaceholder"),
      statusesHintText: tKey("statusesHint"),
      statusesRequiredText: tKey("statusesRequired"),
      prioritiesLabelText: tKey("prioritiesLabel"),
      prioritiesPlaceholderText: tKey("prioritiesPlaceholder"),
      prioritiesHintText: tKey("prioritiesHint"),
      ticketTypesLabelText: tKey("ticketTypesLabel"),
      ticketTypesPlaceholderText: tKey("ticketTypesPlaceholder"),
      ticketTypesHintText: tKey("ticketTypesHint"),
      assignedToMeLabelText: tKey("assignedToMeLabel"),
      assignedToMeHintText: tKey("assignedToMeHint"),
      tagsLabelText: tKey("tagsLabel"),
      tagsPlaceholderText: tKey("tagsPlaceholder"),
      tagsHintText: tKey("tagsHint"),
      viewLabelText: tKey("viewLabel"),
      viewPlaceholderText: tKey("viewPlaceholder"),
      viewHintText: tKey("viewHint"),
      viewRequiredText: tKey("viewRequired"),
      noViewsText: tKey("noViews"),
      doneStatusLabelText: tKey("doneStatusLabel"),
      doneStatusPlaceholderText: tKey("doneStatusPlaceholder"),
      doneStatusHintText: tKey("doneStatusHint"),
      doneStatusRequiredText: tKey("doneStatusRequired"),
      loadingViewsText: tKey("loadingViews"),
      loadErrorText: tKey("loadError"),
      statusLabels: {
        New: tKey("statuses.new"),
        Open: tKey("statuses.open"),
        Pending: tKey("statuses.pending"),
        Hold: tKey("statuses.hold"),
        Solved: tKey("statuses.solved"),
        Closed: tKey("statuses.closed"),
      },
      priorityLabels: {
        Low: tKey("priorities.low"),
        Normal: tKey("priorities.normal"),
        High: tKey("priorities.high"),
        Urgent: tKey("priorities.urgent"),
      },
      ticketTypeLabels: {
        Question: tKey("ticketTypes.question"),
        Incident: tKey("ticketTypes.incident"),
        Problem: tKey("ticketTypes.problem"),
        Task: tKey("ticketTypes.task"),
      },
    };
  }, [t]);
};
